/**
 * Ciclo de vida del portal público: ruta por path, polling guiado por el
 * server y suscripción del widget.
 *
 * Vive con las páginas y no en `src/hooks.ts` por dos razones: es glue
 * exclusivo de esta zona (nada del portal privado lo usa) y así el archivo
 * compartido no se toca. Lo puro —parseo de rutas, formas de respuesta— está
 * en `lib/public-router.ts` y `lib/public-client.ts`, que se testean sin DOM.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { parsePublicPath, type PublicRoute } from '../../lib/public-router';
import { DEFAULT_POLL_MS } from '../../lib/public-client';

// ---------------------------------------------------------------------------
// Ruta por path (con history)
// ---------------------------------------------------------------------------

/**
 * Store observable del `location` para `useSyncExternalStore`.
 *
 * `popstate` cubre el botón de atrás; `scout:navigate` es el evento propio que
 * dispara `navigatePublic` cuando la app hace `pushState` (el browser NO emite
 * `popstate` en ese caso, que es el clásico bug de un router hecho a mano).
 */
function subscribeToLocation(listener: () => void): () => void {
  window.addEventListener('popstate', listener);
  window.addEventListener('scout:navigate', listener);
  return () => {
    window.removeEventListener('popstate', listener);
    window.removeEventListener('scout:navigate', listener);
  };
}

function locationKey(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export function navigatePublic(href: string): void {
  window.history.pushState({}, '', href);
  window.dispatchEvent(new Event('scout:navigate'));
  window.scrollTo({ top: 0 });
}

/** Ruta pública actual, o `null` si esta URL es del portal privado. */
export function usePublicRoute(base: string): PublicRoute | null {
  const key = useSyncExternalStore(subscribeToLocation, locationKey, () => '');
  const [pathname = '', search = ''] = key.split(/(?=\?)/);
  return parsePublicPath(pathname, base, search);
}

// ---------------------------------------------------------------------------
// Polling
// ---------------------------------------------------------------------------

export interface PolledState<T> {
  data: T | null;
  error: Error | null;
  /** `true` sólo la PRIMERA carga: después se muestra el dato viejo. */
  loading: boolean;
  /** Cuándo se recibió el último dato bueno (epoch ms), para el "hace N s". */
  updatedAt: number | null;
  reload: () => void;
}

/** ¿La pestaña está visible? En SSR y en Node, se asume que sí. */
function documentVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function subscribeToVisibility(listener: () => void): () => void {
  if (typeof document === 'undefined') return () => undefined;
  document.addEventListener('visibilitychange', listener);
  return () => document.removeEventListener('visibilitychange', listener);
}

export function useVisible(): boolean {
  return useSyncExternalStore(subscribeToVisibility, documentVisible, () => true);
}

/**
 * Pide `load()` y lo vuelve a pedir cada `pollAfterMs` (el número que manda el
 * server en el cuerpo de cada documento — ver ADR-020).
 *
 * Tres decisiones que se notan en pantalla y en la factura:
 *
 *  1. **El dato viejo se queda** mientras se recarga: el marcador no parpadea a
 *     "cargando" cada 5 segundos.
 *  2. **Un error no borra lo que había.** Si el server se cae en el medio del
 *     partido, el espectador sigue viendo el último marcador con un aviso, no
 *     una pantalla vacía.
 *  3. **La pestaña oculta no pollea**, y al volver refresca de inmediato. Un
 *     portal público vive en pestañas de fondo durante horas.
 */
export function usePolled<T extends { pollAfterMs?: number }>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  /**
   * Pisa el ritmo del server. Único usuario: el widget de overlay, que cuando
   * su SSE está abierto baja el polling a red de seguridad — el marcador ya le
   * llega por el stream y seguir pidiendo cada 5 s sería trabajo al pedo.
   */
  overrideMs?: number,
): PolledState<T> {
  const [state, setState] = useState<{
    data: T | null;
    error: Error | null;
    loading: boolean;
    updatedAt: number | null;
  }>({ data: null, error: null, loading: true, updatedAt: null });

  const [nonce, setNonce] = useState(0);
  const visible = useVisible();
  const loadRef = useRef(load);
  loadRef.current = load;
  const overrideRef = useRef(overrideMs);
  overrideRef.current = overrideMs;

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async (): Promise<void> => {
      try {
        const data = await loadRef.current();
        if (!alive) return;
        setState({ data, error: null, loading: false, updatedAt: Date.now() });
        // El server manda el ritmo. Sin `pollAfterMs` (un API viejo) se cae a
        // un intervalo conservador en vez de martillar.
        timer = setTimeout(
          () => void tick(),
          overrideRef.current ?? data.pollAfterMs ?? DEFAULT_POLL_MS,
        );
      } catch (err) {
        if (!alive) return;
        const error = err instanceof Error ? err : new Error(String(err));
        // El dato anterior NO se borra: se muestra con el aviso al lado.
        setState((prev) => ({ ...prev, error, loading: false }));
        timer = setTimeout(() => void tick(), DEFAULT_POLL_MS);
      }
    };

    if (visible) void tick();

    return () => {
      alive = false;
      if (timer !== null) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, visible]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/**
 * Pide `load()` UNA vez y listo. Para lo que no cambia durante un partido: hoy,
 * la ficha de la organización que pinta el encabezado.
 *
 * Existe aparte de `usePolled` para que la diferencia sea explícita en el
 * código y no un `pollAfterMs` gigante: un portal público abierto en cuarenta
 * mil pestañas no tiene por qué preguntar cada quince segundos de qué color es
 * el logo.
 */
export function useOnce<T>(load: () => Promise<T>, deps: readonly unknown[]): {
  data: T | null;
  error: Error | null;
  loading: boolean;
} {
  const [state, setState] = useState<{ data: T | null; error: Error | null; loading: boolean }>({
    data: null,
    error: null,
    loading: true,
  });
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let alive = true;
    setState((prev) => ({ ...prev, loading: true }));
    loadRef
      .current()
      .then((data) => {
        if (alive) setState({ data, error: null, loading: false });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setState({
          data: null,
          error: err instanceof Error ? err : new Error(String(err)),
          loading: false,
        });
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

/** "hace 4 s" / "hace 2 min" a partir del epoch de la última actualización. */
export function useTicker(everyMs = 1000): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return tick;
}
